// src/services/api/tasks.service.ts

import { apiClient, makeApiRequest } from './client';
import { endpoints } from './config';
import { 
  Task, 
  CreateTaskRequest, 
  UpdateTaskRequest,
  TaskFilters,
  Project,
  OvertimeApprovalRequest,
  TaskApprovalRequest
} from './types';

export class TasksService {
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    return makeApiRequest(() => 
      apiClient.get<Task[]>('/tasks/', filters)
    );
  }

  async getWeeklyTasks(weekStartDate: string): Promise<Task[]> {
    return makeApiRequest(() => 
      apiClient.get<Task[]>(endpoints.tasks.weekly, { week_start_date: weekStartDate })
    );
  }

  async createTask(projectId: number, taskData: CreateTaskRequest & { assigned_employee_ids?: number[] }): Promise<Task> {
    return makeApiRequest(() => 
      apiClient.post<Task>(endpoints.tasks.create, { 
        ...taskData, 
        project_id: projectId,
        assigned_employee_ids: taskData.assigned_employee_ids || []  // Use provided IDs or empty array
      })
    );
  }

  async getTaskById(id: number): Promise<Task> {
    return makeApiRequest(() => 
      apiClient.get<Task>(endpoints.tasks.byId(id))
    );
  }

  async updateTask(id: number, updates: UpdateTaskRequest): Promise<Task> {
    return makeApiRequest(() => 
      apiClient.put<Task>(endpoints.tasks.update(id), updates)
    );
  }

  async deleteTask(id: number): Promise<void> {
    return makeApiRequest(() => 
      apiClient.delete(endpoints.tasks.delete(id))
    );
  }

  async completeTask(id: number): Promise<Task> {
    return makeApiRequest(() => 
      apiClient.put<Task>(endpoints.tasks.update(id), {
        status: 'completed'
      })
    );
  }

  async getTasksByUser(userId: number, filters?: TaskFilters): Promise<{ task: Task; project: Project }[]> {
    return makeApiRequest(() => 
      apiClient.get<{ task: Task; project: Project }[]>(
        endpoints.tasks.byUser(userId), 
        filters
      )
    );
  }

  // Get tasks for a specific user within a specific project
  async getTasksByUserAndProject(userId: number, projectId: number): Promise<Task[]> {
    return makeApiRequest(() => 
      apiClient.get<Task[]>('/tasks/', { 
        project_id: projectId,
        user_id: userId
      })
    );
  }

  // Get all tasks for a project (PM view)
  async getTasksForProject(projectId: number, filters?: TaskFilters): Promise<Task[]> {
    return makeApiRequest(() => 
      apiClient.get<Task[]>(endpoints.projects.tasks, { 
        project_id: projectId,
        ...filters
      })
    );
  }

  // Overtime approval methods
  async approveOvertime(taskId: number, approvalData: OvertimeApprovalRequest): Promise<Task> {
    return makeApiRequest(() => 
      apiClient.post<Task>(`/tasks/${taskId}/approve-overtime`, approvalData)
    );
  }

  // Aprovação gerencial das horas da tarefa (manager/director/admin)
  async approveTask(taskId: number, approvalData: TaskApprovalRequest): Promise<Task> {
    return makeApiRequest(() =>
      apiClient.post<Task>(`/tasks/${taskId}/approve`, approvalData)
    );
  }

  async getPendingOvertimeApprovals(): Promise<Task[]> {
    // Workaround: Use regular tasks endpoint and filter on frontend
    return makeApiRequest(() => 
      apiClient.get<Task[]>('/tasks/', { has_overtime: 'true' })
    );
  }
}

export const tasksService = new TasksService();
